export const DEFAULT_GRID_COLS = 24;
export const DEFAULT_GRID_ROWS = 30;
export const DEFAULT_GRID_TYPE = 'square'; // 'square' | 'hex'

export const getTokenInitialFontSize = (cols) => (
  14 * DEFAULT_GRID_COLS / Math.max(1, Number(cols) || DEFAULT_GRID_COLS)
);

const STORAGE_KEY = 'orchestratorGridConfig';

export const normalizeGridConfig = (config = {}) => {
  const { cols, rows, type } = config || {};
  return {
    cols: Math.max(1, Math.min(100, Number(cols) || DEFAULT_GRID_COLS)),
    rows: Math.max(1, Math.min(100, Number(rows) || DEFAULT_GRID_ROWS)),
    type: type === 'hex' ? 'hex' : DEFAULT_GRID_TYPE,
  };
};

export const getGridTooltipPosition = (token, tokenSize, cols, rows) => {
  const placeRight = token.x + tokenSize < cols * 0.75;
  return {
    position: 'absolute',
    zIndex: 3000,
    ...(placeRight
      ? { left: `calc((${token.x + tokenSize}) * (100% / ${cols}) + 4px)` }
      : { right: `calc((${cols} - ${token.x}) * (100% / ${cols}) + 4px)` }),
    top: `calc(${token.y} * (100% / ${rows}))`,
  };
};

export const loadGridConfig = () => {
  try {
    if (typeof window === 'undefined') {
      return normalizeGridConfig();
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return normalizeGridConfig();
    return normalizeGridConfig(JSON.parse(raw));
  } catch {
    return normalizeGridConfig();
  }
};

export const saveGridConfig = (config) => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }
  } catch {
    // Ignore storage errors
  }
};

// ─── Flat-top hex grid math (even-q offset coordinates) ───────────────────────
//
// Layout: flat edge on top/bottom, vertices on left/right.
// Even columns (0, 2, …) are at normal vertical position; odd columns are
// shifted down by half a hex height.
//
// SVG viewBox coordinates used internally (circumradius r = 1):
//   svgW = 1.5 * cols + 0.5
//   svgH = √3  * (rows + 0.5)
// Hex centre at offset (col, row):
//   cx = 1 + col * 1.5
//   cy = √3/2 + row * √3  +  (col is odd ? √3/2 : 0)

const HEX_SQRT3 = Math.sqrt(3);

/** Natural aspect ratio (width / height) for a cols × rows flat-top hex grid. */
export const hexGridAspectRatio = (cols, rows) => {
  const w = 1.5 * cols + 0.5;
  const h = HEX_SQRT3 * (rows + 0.5);
  return w / h;
};

/**
 * Percentage position [0–100] of the centre of hex cell (col, row) within the
 * board.  Use these values directly for `left` / `top` CSS (centre of token).
 */
export const hexCellToPercent = (col, row, cols, rows) => {
  const w = 1.5 * cols + 0.5;
  const h = HEX_SQRT3 * (rows + 0.5);
  const cx = 1 + col * 1.5;
  const cy = HEX_SQRT3 / 2 + row * HEX_SQRT3 + (col % 2 === 1 ? HEX_SQRT3 / 2 : 0);
  return { pctX: (cx / w) * 100, pctY: (cy / h) * 100 };
};

/**
 * Nearest hex cell (col, row) for a pixel position within the board.
 * Uses fractional axial coordinates + cube-rounding for flat-top even-q offset.
 */
export const nearestHexCell = (pixX, pixY, boardW, boardH, cols, rows) => {
  const w = 1.5 * cols + 0.5;
  const h = HEX_SQRT3 * (rows + 0.5);

  // Convert pixel → SVG coordinates
  const svgX = (pixX / boardW) * w;
  const svgY = (pixY / boardH) * h;

  // Translate so that hex (0, 0) centre is at the origin
  const ax = svgX - 1;
  const ay = svgY - HEX_SQRT3 / 2;

  // Fractional axial (q, r_axial) then fractional cube s = −q − r_axial
  const fq = (2 / 3) * ax;
  const fr = (-1 / 3) * ax + (1 / HEX_SQRT3) * ay;
  const fs = -fq - fr;

  // Cube rounding
  let rq = Math.round(fq);
  let rr = Math.round(fr);
  let rs = Math.round(fs);

  const dq = Math.abs(rq - fq);
  const dr = Math.abs(rr - fr);
  const ds = Math.abs(rs - fs);

  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  else rs = -rq - rr;

  // Convert cube → even-q offset
  // From hexOffsetToCube: cube_r_axial = offset_r − (offset_q − (offset_q & 1)) / 2
  // Inverse:             offset_r      = cube_r_axial + (offset_q − (offset_q & 1)) / 2
  const col = rq;
  const row = rr + (rq - (rq & 1)) / 2;

  return {
    col: Math.max(0, Math.min(cols - 1, col)),
    row: Math.max(0, Math.min(rows - 1, row)),
  };
};

export const getTokenGridPositionFromWorldPoint = (worldPoint, tokenSize, config) => {
  const cols = Number(config?.cols);
  const rows = Number(config?.rows);
  const pointX = Number(worldPoint?.x);
  const pointY = Number(worldPoint?.y);
  if (
    !Number.isFinite(cols) || cols <= 0
    || !Number.isFinite(rows) || rows <= 0
    || !Number.isFinite(pointX) || !Number.isFinite(pointY)
  ) return null;

  if (config.type === 'hex') {
    const width = Number(worldPoint.width);
    const height = Number(worldPoint.height);
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      return null;
    }
    const nearest = nearestHexCell(
      pointX * width,
      pointY * height,
      width,
      height,
      cols,
      rows
    );
    return { x: nearest.col, y: nearest.row };
  }

  const requestedSize = Number(tokenSize);
  const size = Number.isFinite(requestedSize) && requestedSize > 0 ? requestedSize : 1;
  const snapAxis = (value, axisLimit) => {
    const maxStart = Math.max(0, axisLimit - size);
    if (size % 2 === 0) {
      const cross = Math.round(Math.max(0, Math.min(axisLimit, value)));
      return Math.min(maxStart, Math.max(0, cross - size / 2));
    }

    const centerCell = Math.min(axisLimit - 1, Math.max(0, Math.floor(value)));
    const proposed = centerCell - Math.floor(size / 2);
    return Math.min(maxStart, Math.max(0, proposed));
  };

  return {
    x: snapAxis(pointX * cols, cols),
    y: snapAxis(pointY * rows, rows),
  };
};
